import { castDraft, current, produce, type Draft } from 'immer';
import type { GameAction, PlayIntent, ReduceContext, ReduceResult, Reducer } from './actions';
import { isDefusable, isInterrupt, isLethal, isPlayable, isRescue, type CardDef } from './card';
import type { GameEvent } from './events';
import type { CardTitle, CardUid, PlayerId } from './ids';
import { seedFrom } from './rng';
import { createInitialState, validateConfig } from './setup';
import {
  addKnownInDraw,
  addKnownInHand,
  canAct,
  diluteKnownInDraw,
  knownTopAfterDraw,
  knownTopAfterInsert,
  knownTopLearn,
  type GameState,
  type PendingPlay,
  type PlayerState,
} from './state';
import { checkWinner, consumeTurn, discardFromHand, killPlayer } from './turns';
import { registerHouseRule } from './houseRules';
import { t, type TextKey } from '../i18n';

/**
 * 纯函数 reducer —— 本项目的「后端」。
 *
 * ## 三条必须守住的规矩
 *
 * 1. **不读时钟、不用 `Math.random`。** 随机从 `state.rng` 来，种子由 `game/start`
 *    动作带进来。这样一串动作（棋谱）就能完整复现整局（`architecture.md` §3.8）。
 * 2. **非法动作返回 `{ ok: false, reason }`，不抛异常。** 网络延迟会让过期指令到达，
 *    那不该把房主搞崩（`architecture.md` §6.6）。
 * 3. **打出的牌当场进弃牌堆，永不撤销。** 效果等窗口关了再结算，
 *    被休想取消时**根本不执行**（`architecture.md` §3.3）。
 */

type Handler = (
  draft: Draft<GameState>,
  action: GameAction,
  ctx: ReduceContext,
  events: GameEvent[],
) => string | undefined;

// ============================================================
// 小工具
// ============================================================

function playerOf(draft: Draft<GameState>, id: PlayerId): Draft<PlayerState> | undefined {
  return draft.players.find((p) => p.id === id);
}

function titleOf(draft: Draft<GameState>, uid: CardUid): CardTitle | undefined {
  return draft.cards[uid]?.title;
}

function defOf(
  draft: Draft<GameState>,
  registry: ReduceContext['registry'],
  uid: CardUid,
): CardDef | undefined {
  const title = titleOf(draft, uid);
  return title === undefined ? undefined : registry.get(title);
}

function hasTrait(
  draft: Draft<GameState>,
  registry: ReduceContext['registry'],
  uid: CardUid,
  predicate: (def: CardDef) => boolean,
): boolean {
  const def = defOf(draft, registry, uid);
  return def !== undefined && predicate(def);
}

function findInHand(
  draft: Draft<GameState>,
  registry: ReduceContext['registry'],
  player: Draft<PlayerState>,
  predicate: (def: CardDef) => boolean,
): CardUid | undefined {
  return player.hand.find((uid) => hasTrait(draft, registry, uid, predicate));
}

// ============================================================
// 出牌校验
// ============================================================

function sameTitle(draft: Draft<GameState>, cards: readonly CardUid[]): boolean {
  const first = cards[0];
  if (first === undefined) return false;
  const title = titleOf(draft, first);
  if (title === undefined) return false;
  return cards.every((uid) => titleOf(draft, uid) === title);
}

function validatePlay(
  draft: Draft<GameState>,
  actor: PlayerId,
  play: PlayIntent,
  ctx: ReduceContext,
): string | undefined {
  const player = playerOf(draft, actor);
  if (player === undefined) return t('core.reason.playerNotFound');

  const inHand = (uid: CardUid): boolean => player.hand.includes(uid);
  const isOtherAlive = (id: PlayerId): boolean => {
    const target = playerOf(draft, id);
    return target !== undefined && target.alive && target.id !== actor;
  };

  switch (play.kind) {
    case 'single': {
      if (!inHand(play.card)) return t('core.reason.cardNotInHand');
      const def = defOf(draft, ctx.registry, play.card);
      if (def === undefined || !isPlayable(def)) return t('core.reason.cardNotPlayable');
      // ★ 一次出牌必须产生一个"行动"（有效果）。无效牌单独打出 = 非法（评审）。
      if (def.effect === null) return t('core.reason.cardNoEffect');

      if (def.traits.needsTarget === true) {
        if (play.target === undefined || play.target === null) return t('core.reason.cardNeedsTarget');
        if (!isOtherAlive(play.target)) return t('core.reason.targetMustBeAlive');
      } else if (play.target !== undefined && play.target !== null && !isOtherAlive(play.target)) {
        return t('core.reason.targetMustBeAlive');
      }
      return undefined;
    }

    case 'pair': {
      if (!draft.config.combos.twoOfAKind) return t('core.reason.pairDisabled');
      if (!play.cards.every(inHand)) return t('core.reason.someCardsNotInHand');
      if (!sameTitle(draft, play.cards)) return t('core.reason.pairMustMatch');
      if (!isOtherAlive(play.target)) return t('core.reason.targetMustBeAlive');
      return undefined;
    }

    case 'triple': {
      if (!draft.config.combos.threeOfAKind) return t('core.reason.tripleDisabled');
      if (!play.cards.every(inHand)) return t('core.reason.someCardsNotInHand');
      if (!sameTitle(draft, play.cards)) return t('core.reason.tripleMustMatch');
      if (!isOtherAlive(play.target)) return t('core.reason.targetMustBeAlive');
      if (play.namedTitle.length === 0) return t('core.reason.namedTitleRequired');
      return undefined;
    }

    case 'fiveDifferent': {
      if (!draft.config.combos.fiveDifferent) return t('core.reason.fiveDisabled');
      if (play.cards.length !== 5) return t('core.reason.fiveExact');
      if (!play.cards.every(inHand)) return t('core.reason.someCardsNotInHand');

      const titles = play.cards.map((uid) => titleOf(draft, uid));
      if (titles.some((t) => t === undefined)) return t('core.reason.cardNotInRegistry');
      if (new Set(titles).size !== 5) return t('core.reason.fiveDistinct');

      // 候选 = 现在的弃牌堆 + 刚打出的这 5 张（它们结算前就已经进弃牌堆了）
      const candidates = [...draft.discardPile, ...play.cards];
      if (!candidates.includes(play.discardPick)) return t('core.reason.pickNotInDiscard');
      if (
        !draft.config.combos.fiveDifferentCanTakeDefuse &&
        hasTrait(draft, ctx.registry, play.discardPick, isRescue)
      ) {
        return t('core.reason.fiveRescueDisabled');
      }
      return undefined;
    }
  }
}

// ============================================================
// 效果结算
// ============================================================

/** 把玩家的出牌意图固化成待结算的扁平形状 */
function toPending(player: PlayerId, play: PlayIntent): PendingPlay {
  switch (play.kind) {
    case 'single':
      return {
        player,
        cards: [play.card],
        kind: 'single',
        target: play.target ?? null,
        namedTitle: null,
        discardPick: null,
      };
    case 'pair':
      return {
        player,
        cards: [...play.cards],
        kind: 'pair',
        target: play.target,
        namedTitle: null,
        discardPick: null,
      };
    case 'triple':
      return {
        player,
        cards: [...play.cards],
        kind: 'triple',
        target: play.target,
        namedTitle: play.namedTitle,
        discardPick: null,
      };
    case 'fiveDifferent':
      return {
        player,
        cards: [...play.cards],
        kind: 'fiveDifferent',
        target: null,
        namedTitle: null,
        discardPick: play.discardPick,
      };
  }
}

/**
 * 两张同名：让**抽牌的人**从对方手里挑一张牌背。
 *
 * ⚠️ 这里**不再随机抽**了 —— 见 `state.ts` 里 `stealCard` 那一段。
 * 挑的是位次，结算时才被抽的人报上真实顺序。
 */
function applyPair(draft: Draft<GameState>, play: PendingPlay, events: GameEvent[]): void {
  if (play.target === null) return;
  const target = playerOf(draft, play.target);
  if (target === undefined || target.hand.length === 0) return;

  // ★ 只剩一张：没有"挑哪张"的余地，跳过抉择，直接抽走（用户要求）
  if (target.hand.length === 1) {
    const stolen = target.hand[0];
    if (stolen === undefined) return;
    target.hand = [];
    playerOf(draft, play.player)?.hand.push(stolen);
    events.push({ t: 'card/stolen', by: play.player, from: target.id, card: stolen });
    return;
  }

  draft.decision = {
    kind: 'stealCard',
    player: play.player,
    from: target.id,
    count: target.hand.length,
  };
}

/**
 * 三张同名：目标手里有指定牌名就必须交一张，否则什么都不发生。
 *
 * 目标没有 [断点] → 直接执行索取（不留抉择）；
 * 有 [断点] → 开一个 giveCard 抉择，让他选"否决"还是"交牌"（用户要求）。
 */
function applyTriple(
  draft: Draft<GameState>,
  play: PendingPlay,
  ctx: ReduceContext,
  events: GameEvent[],
): void {
  if (play.target === null || play.namedTitle === null) return;

  const target = playerOf(draft, play.target);
  if (target === undefined) return;
  const matching = target.hand.filter((uid) => titleOf(draft, uid) === play.namedTitle);
  if (matching.length === 0) return;

  const hasNope = target.hand.some((uid) => hasTrait(draft, ctx.registry, uid, isInterrupt));
  if (hasNope) {
    draft.decision = {
      kind: 'giveCard',
      player: target.id,
      to: play.player,
      namedTitle: play.namedTitle,
    };
    return;
  }

  // 没有否决：直接拿走一张
  const given = matching[0];
  if (given === undefined) return;
  target.hand = target.hand.filter((uid) => uid !== given);
  playerOf(draft, play.player)?.hand.push(given);
  events.push({ t: 'card/given', by: target.id, to: play.player, card: given });
}

/** 五张不同名：从弃牌堆捞一张（游戏规则） */
function applyFiveDifferent(
  draft: Draft<GameState>,
  play: PendingPlay,
  ctx: ReduceContext,
): void {
  // 候选在结算时才算 —— 此刻那 5 张已经在弃牌堆里了
  const candidates = draft.discardPile.filter((uid) => {
    if (draft.config.combos.fiveDifferentCanTakeDefuse) return true;
    return !hasTrait(draft, ctx.registry, uid, isRescue);
  });

  draft.decision = { kind: 'pickFromDiscard', player: play.player, candidates: [...candidates] };
}

/** 结算一次出牌的效果。**只在响应窗口关闭且没被取消时调用。** */
function applyPlay(
  draft: Draft<GameState>,
  play: PendingPlay,
  ctx: ReduceContext,
  events: GameEvent[],
): void {
  switch (play.kind) {
    case 'pair':
      applyPair(draft, play, events);
      return;
    case 'triple':
      applyTriple(draft, play, ctx, events);
      return;
    case 'fiveDifferent':
      applyFiveDifferent(draft, play, ctx);
      return;
    case 'single': {
      const uid = play.cards[0];
      if (uid === undefined) return;
      const def = defOf(draft, ctx.registry, uid);
      if (def === undefined || def.effect === null) return;

      // ⚠️ 按 ID 查实现 —— 核心不认识任何具体效果
      const implementation = ctx.registry.effect(def.effect);
      implementation?.(draft, events, {
        play,
        card: def,
        registry: ctx.registry,
        actor: play.player,
      });
      return;
    }
  }
}

// ============================================================
// 出牌与响应窗口
// ============================================================

function performPlay(
  draft: Draft<GameState>,
  actor: PlayerId,
  play: PlayIntent,
  events: GameEvent[],
): void {
  const pending = toPending(actor, play);

  // ---- ① 卡牌移动：出牌当场执行，**永不撤销**（architecture.md §3.3）----
  discardFromHand(draft, pending.player, pending.cards);
  events.push({
    t: 'card/played',
    player: pending.player,
    cards: [...pending.cards],
    kind: pending.kind,
    target: pending.target,
  });

  // ★ 所有能通过校验的出牌都产生一个"行动" → 一律进 pending play（可被断点打断）。
  //    没有效果的出牌在校验阶段就被拒了（见 `validatePlay`），不会走到这里。
  //    ⚠️ 抽牌（`turn/draw`）不经过这里，因此**不等待**。
  draft.resolution = castDraft({ play: pending, nopeChain: [], committed: true });
  events.push({ t: 'window/opened', by: pending.player });
}

/** 举起一手牌：牌**不动**，只开打断窗口 */
function armPlay(
  draft: Draft<GameState>,
  actor: PlayerId,
  play: PlayIntent,
  events: GameEvent[],
): void {
  const pending = toPending(actor, play);
  draft.resolution = castDraft({ play: pending, nopeChain: [], committed: false });
  events.push({ t: 'window/opened', by: pending.player });
}

/** 松手提交：牌这时才进弃牌堆（举起阶段可能已被打断、牌已被没收 → 幂等） */
function commitArmed(draft: Draft<GameState>, actor: PlayerId, events: GameEvent[]): void {
  const res = draft.resolution;
  if (res === null || res.committed) return;
  const moved = discardFromHand(draft, actor, res.play.cards);
  if (moved) {
    events.push({
      t: 'card/played',
      player: actor,
      cards: [...res.play.cards],
      kind: res.play.kind,
      target: res.play.target,
    });
  }
  res.committed = true;
}

/** 关闭响应窗口，并按休想链长度的奇偶决定效果生不生效 */
function closeWindow(draft: Draft<GameState>, ctx: ReduceContext, events: GameEvent[]): void {
  const resolution = draft.resolution;
  if (resolution === null) return;

  const cancelled = resolution.nopeChain.length % 2 === 1;
  draft.resolution = null;
  events.push({ t: 'window/closed', cancelled });

  if (!cancelled) applyPlay(draft, resolution.play, ctx, events);

  checkWinner(draft, events);
}

// ============================================================
// 动作处理器
// ============================================================

const handleLobbyJoin: Handler = (draft, action, _ctx, events) => {
  if (action.t !== 'lobby/join') return t('core.reason.actionMismatch');
  if (draft.phase !== 'lobby') return t('core.reason.gameAlreadyStarted');
  if (draft.players.some((p) => p.id === action.actor)) return t('core.reason.alreadyInRoom');

  draft.players.push({
    id: action.actor,
    name: action.name,
    hand: [],
    alive: true,
    connected: true,
  });
  events.push({ t: 'lobby/playerJoined', player: action.actor, name: action.name });
  return undefined;
};

const handleLobbyLeave: Handler = (draft, action, _ctx, events) => {
  if (action.t !== 'lobby/leave') return t('core.reason.actionMismatch');
  if (draft.phase !== 'lobby') return t('core.reason.cannotLeave');

  const index = draft.players.findIndex((p) => p.id === action.actor);
  if (index < 0) return t('core.reason.notInRoom');
  draft.players.splice(index, 1);

  if (draft.host === action.actor) {
    const next = draft.players[0];
    if (next !== undefined) draft.host = next.id;
  }
  events.push({ t: 'lobby/playerLeft', player: action.actor });
  return undefined;
};

const handleLobbySetConfig: Handler = (draft, action, _ctx, events) => {
  if (action.t !== 'lobby/setConfig') return t('core.reason.actionMismatch');
  if (draft.phase !== 'lobby') return t('core.reason.cannotSetConfig');
  if (action.actor !== draft.host) return t('core.reason.hostOnlyConfig');

  draft.config = castDraft(action.config);
  events.push({ t: 'lobby/configChanged', by: action.actor });
  return undefined;
};

const handleGameStart: Handler = (draft, action, ctx, events) => {
  if (action.t !== 'game/start') return t('core.reason.actionMismatch');
  if (draft.phase !== 'lobby') return t('core.reason.gameAlreadyStarted');
  if (action.actor !== draft.host) return t('core.reason.hostOnlyStart');
  if (draft.players.length < 2) return t('core.reason.needTwoPlayers');

  const validation = validateConfig(draft.config, ctx.registry);
  if (!validation.ok) {
    // 把全部问题一次说清楚，房主才知道要改哪几处
    return t('core.reason.configProblems', {
      problems: validation.problems.map((p) => p.message).join(t('common.problemSeparator')),
    });
  }

  const started = createInitialState({
    // ⚠️ 必须 current() 物化 —— draft.config 是代理，不能拿出去冻结
    config: current(draft.config),
    resolved: validation.resolved,
    registry: ctx.registry,
    host: draft.host,
    players: draft.players.map((p) => ({ id: p.id, name: p.name })),
    rng: seedFrom(action.seed),
  });

  draft.phase = started.phase;
  draft.config = castDraft(started.config);
  draft.players = castDraft(started.players);
  draft.cards = castDraft(started.cards);
  draft.drawPile = castDraft(started.drawPile);
  draft.discardPile = castDraft(started.discardPile);
  draft.turn = castDraft(started.turn);
  draft.resolution = castDraft(started.resolution);
  draft.decision = castDraft(started.decision);
  draft.knowledge = castDraft(started.knowledge);
  draft.rng = castDraft(started.rng);
  draft.winner = started.winner;

  events.push({ t: 'game/started' });
  return undefined;
};

const handleTurnPlay: Handler = (draft, action, ctx, events) => {
  if (action.t !== 'turn/play') return t('core.reason.actionMismatch');

  // 举起阶段松手 → 提交（牌这时才进弃牌堆）
  const armed = draft.resolution;
  if (armed !== null && !armed.committed) {
    if (armed.play.player !== action.actor) return t('core.reason.notYourArmedPlay');
    commitArmed(draft, action.actor, events);
    return undefined;
  }

  if (!canAct(draft, action.actor)) return t('core.reason.cannotPlayNow');

  const reason = validatePlay(draft, action.actor, action.play, ctx);
  if (reason !== undefined) return reason;

  performPlay(draft, action.actor, action.play, events);
  return undefined;
};

/** 拖回去：收起"举着"的那一手（牌还在手里，不损失） */
const handleTurnArmCancel: Handler = (draft, action, _ctx, events) => {
  if (action.t !== 'turn/arm/cancel') return t('core.reason.actionMismatch');
  const res = draft.resolution;
  if (res === null || res.committed) return t('core.reason.noArmedPlay');
  if (res.play.player !== action.actor) return t('core.reason.notYourArmedCards');
  const cancelled = res.nopeChain.length % 2 === 1;
  draft.resolution = null;
  events.push({ t: 'window/closed', cancelled });
  return undefined;
};

/** 拖到有效高度：举起这一手，开打断窗口（不移动牌） */
const handleTurnArm: Handler = (draft, action, ctx, events) => {
  if (action.t !== 'turn/arm') return t('core.reason.actionMismatch');
  if (!canAct(draft, action.actor)) return t('core.reason.cannotPlayNow');
  const reason = validatePlay(draft, action.actor, action.play, ctx);
  if (reason !== undefined) return reason;
  armPlay(draft, action.actor, action.play, events);
  return undefined;
};

const handleTurnDraw: Handler = (draft, action, ctx, events) => {
  if (action.t !== 'turn/draw') return t('core.reason.actionMismatch');
  if (!canAct(draft, action.actor)) return t('core.reason.cannotDrawNow');

  if (draft.drawPile.length === 0) {
    // 官方保证不会发生；真发生了就中止并宣告平局，不偷偷洗牌重来（rules.md §9.6）
    draft.phase = 'finished';
    draft.turn = null;
    draft.winner = null;
    return undefined;
  }

  const drawCountBefore = draft.drawPile.length;
  const uid = draft.drawPile.pop();
  const player = playerOf(draft, action.actor);
  if (uid === undefined || player === undefined) return t('core.reason.drawFailed');
  /**
   * ★ **更新每个人的知识。** 三层各自处理：
   * - 定位：`knownTop` 挪位（抽走的是堆顶）；
   * - 区域（在牌堆里）：按 `(D−1)/D` 稀释，被抽走的期望部分进抽者的手牌；
   * - 别人抽走我**定位**的顶牌 → 升级成「定位在抽者手里」。
   * 抽者本人不需要 `knownInHand`（那是他自己的手牌）。
   */
  const factor = drawCountBefore > 0 ? (drawCountBefore - 1) / drawCountBefore : 0;
  for (const key of Object.keys(draft.knowledge)) {
    const known = draft.knowledge[key];
    if (known === undefined) continue;
    const top = known.knownTop.find((card) => card.indexFromTop === 0);
    const { knowledge: diluted, drawn } = diluteKnownInDraw(known, factor);
    let next = diluted;
    if (key !== action.actor) {
      for (const entry of drawn) next = addKnownInHand(next, action.actor, entry.title, entry.count);
      if (top !== undefined) next = addKnownInHand(next, action.actor, top.title, 1);
    }
    draft.knowledge[key] = castDraft(knownTopAfterDraw(next));
  }

  // 抽到的牌先进手牌 —— 连致命牌也是。官方说"Discard all of your cards,
  // including the Exploding Kitten"，说明它当时确实在手里。
  player.hand.push(uid);

  const def = defOf(draft, ctx.registry, uid);
  if (def === undefined || !isLethal(def)) {
    events.push({ t: 'card/drawn', player: player.id, card: uid, secret: true });
    consumeTurn(draft, events);
    return undefined;
  }

  // ---- 致命结算 ----
  events.push({ t: 'lethal/drawn', player: player.id, card: uid });
  events.push({ t: 'card/revealed', card: uid, title: def.id });

  // ⚠️ 必须先看这张致命牌**能不能被拆**（`traits.lethal.defusable`）。
  // 内爆猫就是 `defusable: false` —— 手上握着救命牌也没用。
  //
  // 漏掉这个判断的后果很隐蔽：`defusable` 这个 trait 会被**静默忽略**，
  // 扩展包作者写了 `false`，游戏里却照样问"要不要用救命牌"，
  // 而且用了还真能活。这种"声明了但不生效"的扩展点是最难查的一类 bug。
  const rescue = isDefusable(def) ? findInHand(draft, ctx.registry, player, isRescue) : undefined;
  if (rescue !== undefined) {
    // 允许主动送死（rules.md §9.7），所以这里只是"问一下"，不替他决定
    draft.decision = { kind: 'useRescue', player: player.id, card: uid };
    return undefined;
  }

  killPlayer(draft, player.id, events);
  events.push({ t: 'lethal/exploded', player: player.id, card: uid });
  if (draft.phase === 'playing') consumeTurn(draft, events);
  return undefined;
};

const handleWindowNope: Handler = (draft, action, ctx, events) => {
  if (action.t !== 'window/nope') return t('core.reason.actionMismatch');

  const resolution = draft.resolution;
  const decision = draft.decision;

  // 被抽 / 被要求给牌时打出的否决：直接打断这次行动（没有出牌窗口）
  if (resolution === null) {
    if (decision === null || (decision.kind !== 'stealCard' && decision.kind !== 'giveCard')) {
      return t('core.reason.noResponseWindow');
    }
    if (decision.kind === 'stealCard' && decision.from !== action.actor) {
      return t('core.reason.onlyStolenCanNope');
    }
    if (decision.kind === 'giveCard' && decision.player !== action.actor) {
      return t('core.reason.onlyAskedCanNope');
    }
    const player = playerOf(draft, action.actor);
    if (player === undefined) return t('core.reason.playerNotFound');
    if (!player.hand.includes(action.card)) return t('core.reason.cardNotInHand');
    if (!hasTrait(draft, ctx.registry, action.card, isInterrupt)) return t('core.reason.cardCannotInterrupt');

    discardFromHand(draft, action.actor, [action.card]);
    draft.decision = null;
    events.push({ t: 'window/nope', player: action.actor, card: action.card });
    return undefined;
  }

  const player = playerOf(draft, action.actor);
  if (player === undefined) return t('core.reason.playerNotFound');
  if (!player.alive && !draft.config.deadPlayersCanNope) return t('core.reason.youAreOut');
  if (!player.hand.includes(action.card)) return t('core.reason.cardNotInHand');
  if (!hasTrait(draft, ctx.registry, action.card, isInterrupt)) return t('core.reason.cardCannotInterrupt');

  // ★ **举起阶段被打断** → 没收"举着"的那手牌：行动者失去正在拖拽的那张（用户要求）
  if (!resolution.committed) {
    discardFromHand(draft, resolution.play.player, resolution.play.cards);
  }

  // ⚠️ 断点牌同样当场进弃牌堆，**永不撤销**（官方："Leave them in the Discard Pile"）
  discardFromHand(draft, action.actor, [action.card]);
  resolution.nopeChain.push({ player: action.actor, card: action.card });

  events.push({ t: 'window/nope', player: action.actor, card: action.card });
  return undefined;
};

const handleWindowTimeout: Handler = (draft, action, ctx, events) => {
  if (action.t !== 'window/timeout') return t('core.reason.actionMismatch');
  if (action.actor !== draft.host) return t('core.reason.hostOnlyCloseWindow');
  if (draft.resolution === null) return t('core.reason.noResponseWindow');

  // ★ 举起后一直没松手、也没人打断 → 收起，牌还在手里（不损失）
  if (!draft.resolution.committed && draft.resolution.nopeChain.length === 0) {
    draft.resolution = null;
    events.push({ t: 'window/closed', cancelled: false });
    return undefined;
  }

  closeWindow(draft, ctx, events);
  return undefined;
};

const handleDecide: Handler = (draft, action, ctx, events) => {
  if (action.t !== 'decide') return t('core.reason.actionMismatch');

  const decision = draft.decision;
  if (decision === null) return t('core.reason.noDecision');
  if (decision.player !== action.actor) return t('core.reason.notYourDecision');
  if (decision.kind !== action.decision.kind) return t('core.reason.decisionKindMismatch');

  draft.decision = null;

  switch (decision.kind) {
    case 'useRescue': {
      if (action.decision.kind !== 'useRescue') return t('core.reason.decisionKindMismatch');
      const player = playerOf(draft, action.actor);
      if (player === undefined) return t('core.reason.playerNotFound');

      if (!action.decision.use) {
        // 主动送死
        killPlayer(draft, player.id, events);
        events.push({ t: 'lethal/exploded', player: player.id, card: decision.card });
        if (draft.phase === 'playing') consumeTurn(draft, events);
        return undefined;
      }

      const rescue = findInHand(draft, ctx.registry, player, isRescue);
      if (rescue === undefined) return t('core.reason.noRescueCard');
      discardFromHand(draft, player.id, [rescue]);
      draft.decision = { kind: 'insertLethal', player: player.id, card: decision.card };
      return undefined;
    }

    case 'stealCard': {
      if (action.decision.kind !== 'stealCard') return t('core.reason.decisionKindMismatch');
      const { index, order } = action.decision;
      const target = playerOf(draft, decision.from);
      if (target === undefined) return t('core.reason.stealTargetMissing');
      if (!Number.isInteger(index) || index < 0 || index >= target.hand.length) {
        return t('core.reason.stealIndexRange', { max: target.hand.length - 1 });
      }

      // ⚠️ **被抽的人报上来的顺序**。校验它是目标手牌的一个**排列** ——
      // 报一个不存在的牌、或者少报一张，都要被挡下来
      if (order !== undefined) {
        if (order.length !== target.hand.length) return t('core.reason.orderLengthMismatch');
        const pool = [...target.hand].sort((a, b) => a - b);
        const claimed = [...order].sort((a, b) => a - b);
        if (pool.length !== claimed.length || pool.some((uid, i) => uid !== claimed[i])) {
          return t('core.reason.orderNotPermutation');
        }
        // 认下这个顺序：host 的状态从此与本人的真实排列一致
        target.hand = [...order];
      }

      const stolen = target.hand[index];
      if (stolen === undefined) return t('core.reason.thatCardNotInHand');
      target.hand.splice(index, 1);
      playerOf(draft, decision.player)?.hand.push(stolen);
      events.push({ t: 'card/stolen', by: decision.player, from: target.id, card: stolen });
      return undefined;
    }

    case 'insertLethal': {
      if (action.decision.kind !== 'insertLethal') return t('core.reason.decisionKindMismatch');
      const { index } = action.decision;
      if (!Number.isInteger(index) || index < 0 || index > draft.drawPile.length) {
        return t('core.reason.insertIndexRange', { max: draft.drawPile.length });
      }

      const player = playerOf(draft, action.actor);
      if (player === undefined) return t('core.reason.playerNotFound');
      if (!player.hand.includes(decision.card)) return t('core.reason.lethalNotInHand');

      // 从手里拿出来，插回抽牌堆的任意位置（私密）
      player.hand = player.hand.filter((uid) => uid !== decision.card);
      const oldLength = draft.drawPile.length;
      draft.drawPile.splice(index, 0, decision.card);
      // ★ 插入会挪动所有人的「已知牌序」；而**插入的人**还额外知道这张牌在哪
      for (const key of Object.keys(draft.knowledge)) {
        const known = draft.knowledge[key];
        if (known !== undefined) draft.knowledge[key] = castDraft(knownTopAfterInsert(known, oldLength, index));
      }
      const insertedTitle = titleOf(draft, decision.card);
      const actorKnowledge = draft.knowledge[action.actor];
      if (insertedTitle !== undefined && actorKnowledge !== undefined) {
        draft.knowledge[action.actor] = castDraft(knownTopLearn(actorKnowledge, oldLength - index, insertedTitle));
      }
      // 别人不知道插在哪，但（致命牌是公开的）知道「它现在在抽牌堆里」→ 区域知识
      if (insertedTitle !== undefined) {
        for (const key of Object.keys(draft.knowledge)) {
          if (key === action.actor) continue;
          const known = draft.knowledge[key];
          if (known !== undefined) draft.knowledge[key] = castDraft(addKnownInDraw(known, insertedTitle, 1));
        }
      }
      events.push({ t: 'lethal/defused', player: player.id, card: decision.card });

      consumeTurn(draft, events);
      return undefined;
    }

    case 'giveCard': {
      if (action.decision.kind !== 'giveCard') return t('core.reason.decisionKindMismatch');
      const { card } = action.decision;

      const giver = playerOf(draft, action.actor);
      if (giver === undefined) return t('core.reason.playerNotFound');
      if (!giver.hand.includes(card)) return t('core.reason.cardNotInHand');
      if (decision.namedTitle !== null && titleOf(draft, card) !== decision.namedTitle) {
        return t('core.reason.mustGiveNamed', { title: decision.namedTitle });
      }

      giver.hand = giver.hand.filter((uid) => uid !== card);
      playerOf(draft, decision.to)?.hand.push(card);
      events.push({ t: 'card/given', by: giver.id, to: decision.to, card });
      return undefined;
    }

    case 'pickFromDiscard': {
      if (action.decision.kind !== 'pickFromDiscard') return t('core.reason.decisionKindMismatch');
      const { card } = action.decision;
      if (!decision.candidates.includes(card)) return t('core.reason.cardNotCandidate');

      const index = draft.discardPile.indexOf(card);
      if (index < 0) return t('core.reason.cardNotInDiscard');

      draft.discardPile.splice(index, 1);
      playerOf(draft, action.actor)?.hand.push(card);
      events.push({ t: 'card/reclaimed', by: action.actor, card });
      return undefined;
    }
  }
};

/**
 * 挪一下手牌顺序。
 *
 * ⚠️ **不检查回合、不检查窗口、不检查抉择** —— 这是刻意的。
 * 理牌在物理上是随时可做的，而且它**不可能改变局面**：
 * 只是同一个数组的一次置换，随机数、回合、胜负、手牌多重集全都不动。
 * 把它挡在回合外，唯一的后果是"被抽牌的人没法在对方动手前理牌"，
 * 而那正是这一条要保住的东西。
 */
const handleHandReorder: Handler = (draft, action, _ctx, events) => {
  if (action.t !== 'hand/reorder') return t('core.reason.actionMismatch');
  const player = playerOf(draft, action.actor);
  if (player === undefined) return t('core.reason.playerNotAtTable');

  const hand = player.hand;
  const from = action.from;
  const to = action.to;
  if (!Number.isInteger(from) || !Number.isInteger(to)) return t('core.reason.positionInteger');
  if (from < 0 || from >= hand.length) return t('core.reason.noSuchHandIndex');
  if (to < 0 || to >= hand.length) return t('core.reason.noSuchHandIndex');
  if (from === to) return undefined; // 原位：合法但什么都不做

  const moved = hand[from];
  if (moved === undefined) return t('core.reason.noSuchHandIndex');
  hand.splice(from, 1);
  hand.splice(to, 0, moved);

  events.push({ t: 'hand/reordered', player: action.actor, from, to });
  return undefined;
};

const handleNetStatus: Handler = (draft, action, _ctx, _events) => {
  if (action.t !== 'net/connected' && action.t !== 'net/disconnected') {
    return t('core.reason.actionMismatch');
  }
  const player = playerOf(draft, action.actor);
  if (player === undefined) return t('core.reason.playerNotFound');

  player.connected = action.t === 'net/connected';
  return undefined;
};

// ============================================================
// 分发
// ============================================================

function dispatch(
  draft: Draft<GameState>,
  action: GameAction,
  ctx: ReduceContext,
  events: GameEvent[],
): string | undefined {
  switch (action.t) {
    case 'lobby/join':
      return handleLobbyJoin(draft, action, ctx, events);
    case 'lobby/leave':
      return handleLobbyLeave(draft, action, ctx, events);
    case 'lobby/setConfig':
      return handleLobbySetConfig(draft, action, ctx, events);
    case 'game/start':
      return handleGameStart(draft, action, ctx, events);
    case 'turn/play':
      return handleTurnPlay(draft, action, ctx, events);
    case 'turn/arm':
      return handleTurnArm(draft, action, ctx, events);
    case 'turn/arm/cancel':
      return handleTurnArmCancel(draft, action, ctx, events);
    case 'turn/draw':
      return handleTurnDraw(draft, action, ctx, events);
    case 'window/nope':
      return handleWindowNope(draft, action, ctx, events);
    case 'window/timeout':
      return handleWindowTimeout(draft, action, ctx, events);
    case 'decide':
      return handleDecide(draft, action, ctx, events);
    case 'hand/reorder':
      return handleHandReorder(draft, action, ctx, events);
    case 'net/connected':
    case 'net/disconnected':
      return handleNetStatus(draft, action, ctx, events);
  }
}

export const reduce: Reducer = (
  state: GameState,
  action: GameAction,
  ctx: ReduceContext,
): ReduceResult => {
  const events: GameEvent[] = [];
  let rejection: string | undefined;

  const next = produce(state, (draft) => {
    rejection = dispatch(draft, action, ctx, events);
  });

  // 被拒的动作不改变状态 —— immer 产出的那个副本直接丢掉
  if (rejection !== undefined) return { ok: false, reason: rejection };
  return { ok: true, state: next, events };
};


// ============================================================
// 游戏规则登记（连击 / 出局打断）
// ============================================================
//
// ⚠️ 这两个字段的语义都在这个文件里（validatePlay / applyPair / applyTriple
// / handleWindowNope），所以登记也放这里。见 core/houseRules.ts 的说明。

// ⚠️ 存键不存文案：登记在导入期，t() 会冻住语言。渲染时再 t()（见 room.ts）
const ADVANCED_SECTION: TextKey = 'core.section.advanced';
const MISC_SECTION: TextKey = 'core.section.misc';

registerHouseRule({
  id: 'twoOfAKind',
  section: ADVANCED_SECTION,
  label: 'core.rule.twoOfAKind',
  note: 'core.rule.twoOfAKind.note',
  control: { kind: 'toggle' },
  value: (c) => c.combos.twoOfAKind,
  patch: (v) => ({ combos: { twoOfAKind: Boolean(v) } }),
});

registerHouseRule({
  id: 'threeOfAKind',
  section: ADVANCED_SECTION,
  label: 'core.rule.threeOfAKind',
  note: 'core.rule.threeOfAKind.note',
  control: { kind: 'toggle' },
  value: (c) => c.combos.threeOfAKind,
  patch: (v) => ({ combos: { threeOfAKind: Boolean(v) } }),
});

registerHouseRule({
  id: 'fiveDifferent',
  section: ADVANCED_SECTION,
  label: 'core.rule.fiveDifferent',
  note: 'core.rule.fiveDifferent.note',
  control: { kind: 'toggle' },
  value: (c) => c.combos.fiveDifferent,
  patch: (v) => ({ combos: { fiveDifferent: Boolean(v) } }),
});

registerHouseRule({
  id: 'fiveDifferentCanTakeDefuse',
  section: ADVANCED_SECTION,
  label: 'core.rule.fiveDifferentCanTakeDefuse',
  control: { kind: 'toggle' },
  value: (c) => c.combos.fiveDifferentCanTakeDefuse,
  patch: (v) => ({ combos: { fiveDifferentCanTakeDefuse: Boolean(v) } }),
  enabled: (c) => c.combos.fiveDifferent,
});

registerHouseRule({
  id: 'deadPlayersCanNope',
  section: MISC_SECTION,
  label: 'core.rule.deadPlayersCanNope',
  control: { kind: 'toggle' },
  value: (c) => c.deadPlayersCanNope,
  patch: (v) => ({ deadPlayersCanNope: Boolean(v) }),
});
